import React from 'react';
import Modal from 'react-modal';
import {useRouter} from 'next/router';
import Link from './Link';

import {FacebookButton, GoogleButton, LinkedInButton} from './SocialButton';
import Button from './Button';
import {useAppContext, useAnalytics} from '../hooks';
import {Close} from './ModalNew';
import {getBrandName} from './Brand';

const SignupModal = ({
  isOpen, source, onClose, dismissible = false, returnPath: returnPathProp, eventProps = {},
}) => {
  const {asPath} = useRouter();
  const pathname = returnPathProp || asPath;
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  const resumesWallCopy = {
    header: 'Become a member to open every resume',
    subHeader: 'Create a free account to get started',
    cta: 'Create a free account',
    ctaURL: '/signup',
  };

  const sourceTypes = {
    lesson: {
      header: 'Create a free account to continue learning',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.lessonClickSignupWall,
    },
    course: {
      header: 'Create a free account to continue learning',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.courseClickSignupWall,
    },
    guides: {
      header: 'Create a free account to continue learning',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.guidesClickSignupWall,
    },
    socrates: {
      header: 'Create a free account to start practicing',
      subHeader: 'Get free, unlimited access to our interview question database',
      cta: 'Create a free account',
      ctaURL: '/signup',
      onClick: tracker.questionClickSignupWall,
    },
    duet: {
      header: 'Create an account to start practicing',
      subHeader: 'Practice mock interviews with peers and AI',
      cta: 'Create a free account',
      ctaURL: '/signup',
      onClick: tracker.duetClickSignupWall,
    },
    guideSuggestEdits: {
      header: 'Create a free account to suggest edits',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.guideSuggestEditsClickSignupWall,
    },
    resumes: {...resumesWallCopy, onClick: tracker.guideResumesClickSignupWall},
    resumesFreeUsed: {
      ...resumesWallCopy,
      header: 'You\'ve used your free resume',
      subHeader: 'Members open every resume. Create a free account to get started.',
      onClick: tracker.guideResumesClickSignupWall,
    },
    resumesHub: {...resumesWallCopy, onClick: tracker.resumesHubClickSignupWall},
    experiences: {
      header: 'Create a free account to explore interview experiences',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.experiencesClickSignupWall,
    },
    contribute: {
      header: 'Create a free account to share your experience',
      subHeader: 'Sign up only takes a few seconds',
      onClick: tracker.contributeClickSignupWall,
    },
    chat: {
      header: 'Create a free account to chat with Ace',
      subHeader: 'Ask our AI anything about interview prep — sign up only takes a few seconds',
      onClick: tracker.aiChatClickSignupWall,
    },
  };

  const {
    header,
    subHeader,
    ctaURL,
    cta,
    onClick,
  } = sourceTypes[source];

  return (
    <Modal
      overlayClassName="fixed inset-0 bg-black/40 z-50"
      className="bg-white fixed inset-0 z-50 flex overflow-y-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:inset-auto sm:w-full sm:max-w-xl sm:max-h-screen sm:overflow-hidden sm:rounded-lg sm:shadow-lg focus:outline-none"
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={dismissible}
      shouldCloseOnEsc={dismissible}
    >
      <div className="p-10 w-full max-w-2xl mx-auto text-center">
        {dismissible && <Close onClick={onClose} />}
        <h2 className="mb-2 text-2xl text-gray-900 font-bold leading-none">{header}</h2>
        <h3 className="text-sm font-medium text-gray-400">{subHeader}</h3>
        {currentUser ? (
          <div className="mb-6 flex flex-col max-w-sm mx-auto justify-center gap-2">
            <Button href={ctaURL}>{cta}</Button>
          </div>
        ) : (
          <>
            <div className="my-6 flex flex-col max-w-sm mx-auto justify-center gap-2">
              <GoogleButton
                returnPath={pathname}
                onClick={() => onClick({type: 'google', ...eventProps})}
              >
                Continue with Google
              </GoogleButton>
              <FacebookButton
                returnPath={pathname}
                onClick={() => onClick({type: 'facebook', ...eventProps})}
              >
                Continue with Facebook
              </FacebookButton>
              <LinkedInButton
                returnPath={pathname}
                onClick={() => onClick({type: 'linkedin', ...eventProps})}
              >
                Continue with LinkedIn
              </LinkedInButton>
            </div>
            <div className="text-gray-700 font-medium">
              {`Already use ${getBrandName()}?`}
&nbsp;
              <Link
                className="text-indigo font-semibold"
                href={`/login?returnPath=${pathname}`}
                onClick={() => onClick({type: 'email_password', ...eventProps})}
              >
                Sign in here
              </Link>
              .
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SignupModal;
